import { Flex, Menu, NumberInput, Portal, Tag, Text } from '@chakra-ui/react';
import { useCallback } from 'react';
import { LuChevronDown } from 'react-icons/lu';

interface Props {
  label: string;
  value: string;
  onValueChange: (details: { value: string }) => void;
  formatOptions?: Intl.NumberFormatOptions;
  // When provided, an inline unit picker is appended to the input.
  unit?: string;
  unitOptions?: readonly string[];
  onUnitChange?: (unit: string) => void;
}

const ConditionTextInput = ({
  label,
  value,
  onValueChange,
  formatOptions,
  unit,
  unitOptions,
  onUnitChange,
}: Props) => {
  const onSelectUnit = useCallback(
    (details: { value: string }) => {
      onUnitChange?.(details.value);
    },
    [onUnitChange],
  );

  const showUnitPicker = unitOptions != null && unitOptions.length > 0;

  return (
    <Flex direction="row" gap={2} alignItems="center">
      <Tag.Root bg="surface.muted" borderColor="border.light" borderWidth="1px" borderRadius="xl">
        <Tag.Label>{label}</Tag.Label>
      </Tag.Root>
      <NumberInput.Root
        size="xs"
        value={value}
        onValueChange={onValueChange}
        formatOptions={formatOptions}
      >
        <NumberInput.Input borderRadius="lg" overflow="hidden" borderColor="border.light" />
        <NumberInput.Control
          borderTopRightRadius="xl"
          borderBottomRightRadius="xl"
          overflow="hidden"
        />
      </NumberInput.Root>
      {showUnitPicker && (
        <Menu.Root onSelect={onSelectUnit} positioning={{ placement: 'bottom-end' }}>
          <Menu.Trigger asChild>
            <Flex
              as="button"
              alignItems="center"
              gap={1}
              fontSize="xs"
              fontWeight="medium"
              bg="surface.muted"
              borderWidth="1px"
              borderColor="border.light"
              borderRadius="lg"
              px={2}
              py={1}
              cursor="pointer"
              flexShrink={0}
              whiteSpace="nowrap"
              _hover={{ bg: 'surface.accent' }}
            >
              <Text>{unit ?? 'unit'}</Text>
              <LuChevronDown size={12} />
            </Flex>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content bg="surface.background" borderRadius="lg" maxH="240px" overflowY="auto">
                {unitOptions.map((option) => (
                  <Menu.Item key={option} value={option} fontSize="sm">
                    {option}
                    {unit === option ? ' ✓' : ''}
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      )}
    </Flex>
  );
};

export default ConditionTextInput;
